import { allMusicServices as musicServices } from 'fym-shared/src/music-services/allMusicServices';
import { MusicService } from 'fym-shared/src/music-services/types';
import { MusicAPIIntegrationID, isDemoOnlyImporterId } from 'fym-shared/src/music-services/ids';

export enum SearchResponseStatus {
  success = 'success',
  error = 'error',
  not_found = 'not_found',
}

export enum SearchType {
  unknown = 'unknown',
  track = 'track',
  album = 'album',
  artist = 'artist',
}

export type SearchResponseTrack = {
  name: string;
  externalId: string | null;
  albumName: string | null;
  artistNames: string[] | null;
  isrc: string | null;
  imageUrl: string | null;
  duration: number | null;
  previewUrl: string | null;
  url: string | null;
};

export type SearchResponseCollection = {
  name: string;
  externalId: string | null;
  artistNames: string[] | null;
  itemCount: number | null;
  collectionType: number | null;
  followers: number | null;
  imageUrl: string | null;
  url: string | null;
};

export type SearchResult<T> = {
  status: SearchResponseStatus;
  source?: MusicAPIIntegrationID;
  type: SearchType;
  data: T | null;
};

export type SearchResponse = {
  tracks?: SearchResult<SearchResponseTrack>[];
  albums?: SearchResult<SearchResponseCollection>[];
};

/**
 * Services a search cannot run on.
 *
 * A search needs credentials: only Spotify, Apple Music and YouTube have a
 * public catalogue API, and every other service is searched through the
 * company account musicapi holds for it (`allIntegrationImporters`). These
 * services have no such account. They used to be searched with credentials
 * the FYM app synced to musicapi; that sync is gone (2026-09-18), so a search
 * on them could only ever answer with an error. They stay in
 * `availableSearchSourcesAccept` because inspecting a public URL of theirs
 * needs no credentials.
 */
export const searchSourcesWithoutCompanyAccount: readonly MusicAPIIntegrationID[] = [
  MusicAPIIntegrationID.Pandora,
  MusicAPIIntegrationID.YoutubeMusic,
  MusicAPIIntegrationID.QQMusic,
  MusicAPIIntegrationID.YandexMusic,
  MusicAPIIntegrationID.VKMusic,
  MusicAPIIntegrationID.Zvuk,
  MusicAPIIntegrationID.Gaana,
  MusicAPIIntegrationID.JioSaavn,
];

export const availableSearchSources = musicServices
  // The demo's two ends are addressable services to the transfer engine and
  // nothing at all to the outside world: no public API, no catalogue, nothing
  // to search. Advertising them here would offer callers a source that can
  // only ever answer with an error.
  .filter((service) => !isDemoOnlyImporterId(service.importerID))
  .map((service) => service.musicAPIID)
  .filter((s): s is MusicAPIIntegrationID => s !== undefined)
  .filter((s) => !searchSourcesWithoutCompanyAccount.includes(s));

/**
 * The ids a search request may name as a source: every spelling of every
 * service in `availableSearchSources`. `availableSearchSourcesAccept` below is
 * the wider map for URL inspection and playlist ingestion.
 */
export const searchableSourcesAccept: Record<string, MusicService> = {};

musicServices
  .filter((ms) => ms.musicAPIID !== undefined && availableSearchSources.includes(ms.musicAPIID))
  .forEach((ms) => {
    if (ms.musicAPIID) {
      searchableSourcesAccept[ms.musicAPIID] = ms;
    }
    searchableSourcesAccept[ms.importerID] = ms;
    searchableSourcesAccept[ms.id] = ms;
  });

export const availableSearchSourcesAccept: Record<string, MusicService> = {};

musicServices
  .filter((ms) => ms.musicAPIID !== undefined && !isDemoOnlyImporterId(ms.importerID))
  .forEach((ms) => {
    if (ms.musicAPIID) {
      availableSearchSourcesAccept[ms.musicAPIID] = ms;
    }
    availableSearchSourcesAccept[ms.importerID] = ms;
    availableSearchSourcesAccept[ms.id] = ms;
  });

export const availableSearchTypes = Object.values(SearchType).filter((type) => type !== SearchType.unknown);
